import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

// ─── Currency Config (keep existing logic) ───────────────────────────────────
const countryConfig: Record<string, { currency: string; symbol: string; locale: string; rate: number }> = {
  'MX': { currency: 'MXN', symbol: '$', locale: 'es-MX', rate: 17 },
  'VE': { currency: 'USD', symbol: '$', locale: 'es-VE', rate: 1 },
  'CO': { currency: 'COP', symbol: '$', locale: 'es-CO', rate: 3900 },
  'US': { currency: 'USD', symbol: '$', locale: 'en-US', rate: 1 },
  'default': { currency: 'USD', symbol: '$', locale: 'es', rate: 1 },
};

// FIX 2026-09-18: ALL displayed prices are derived from /api/billing/plans
// (plan_definitions.pricing_brackets). No hardcoded rates — skeleton/'—' while loading.
type Bracket = { min_units: number; max_units?: number | null; flat_cents?: number | null; price_per_unit_cents?: number | null };

function bracketsOf(plan: any): Bracket[] {
  const b = plan?.pricing_brackets || plan?.limits?.pricing_brackets;
  return Array.isArray(b) ? b : [];
}

// Mirrors backend amountFromBrackets (plan-source.js): flat_cents when present, else
// units × price_per_unit_cents, with the monotonic clamp — the price must never
// DECREASE as unit count increases across bracket boundaries (boundary plateaus).
function bracketPriceAt(brackets: Bracket[], u: number): number | null {
  const row = (brackets || []).find(b => u >= Number(b.min_units) && (b.max_units == null || u <= Number(b.max_units)));
  if (!row) return null;
  if (row.flat_cents != null && Number.isFinite(Number(row.flat_cents))) return Number(row.flat_cents);
  if (row.price_per_unit_cents != null && Number.isFinite(Number(row.price_per_unit_cents))) return u * Number(row.price_per_unit_cents);
  return null;
}
function condoPriceFromBrackets(brackets: Bracket[], units: number): number | null {
  const u = Math.max(0, Math.floor(units));
  const direct = bracketPriceAt(brackets, u);
  if (direct == null) return null;
  let max = direct;
  for (const b of (brackets || [])) {
    if (b.max_units == null) break;
    const cap = Number(b.max_units);
    if (!Number.isFinite(cap) || cap >= u) break;
    const boundary = bracketPriceAt(brackets, cap);
    if (boundary != null && boundary > max) max = boundary;
  }
  return max / 100;
}

// Tier description generated FROM the brackets array (null max_units -> ∞).
function tierLine(brackets: Bracket[]): string | null {
  if (!brackets.length) return null;
  const parts = brackets.map(b => {
    const min = Number(b.min_units);
    const max = b.max_units == null ? null : Number(b.max_units);
    const range = `${min}\u2013${max ?? '\u221e'}u`;
    if (b.flat_cents != null && Number.isFinite(Number(b.flat_cents))) return `${range}: precio fijo $${(Number(b.flat_cents) / 100).toFixed(2)}`;
    if (b.price_per_unit_cents != null && Number.isFinite(Number(b.price_per_unit_cents))) return `${range}: $${(Number(b.price_per_unit_cents) / 100).toFixed(2)} c/u`;
    return range;
  });
  return parts.join(' \u00b7 ') + '. El precio nunca baja al subir unidades: al cruzar un tramo, se mantiene el precio alcanzado hasta que la nueva tarifa lo supere.';
}

// Smallest unit count > current where the raw (unclamped) bracket price exceeds the
// current clamped price; null when no improvement exists or no plateau is active.
function nextImprovementUnits(brackets: Bracket[], units: number, clamped: number): number | null {
  const u = Math.max(0, Math.floor(units));
  for (let i = u + 1; i <= u + 10000; i++) {
    const raw = bracketPriceAt(brackets, i);
    if (raw != null && raw / 100 > clamped) return i;
  }
  return null;
}

const isBrowser = typeof window !== 'undefined';

function detectCountry(): string {
  if (!isBrowser) return 'default';
  try {
    const lang = navigator.language || '';
    if (lang.includes('es-MX')) return 'MX';
    if (lang.includes('es-VE')) return 'VE';
    if (lang.includes('es-CO')) return 'CO';
    if (lang.includes('en-US')) return 'US';
    return 'default';
  } catch { return 'default'; }
}

function formatPrice(amount: number, countryCode: string): string {
  const cfg = countryConfig[countryCode] || countryConfig.default;
  const n = amount * cfg.rate;
  const grouped = n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${cfg.symbol}${grouped}`;
}

// ─── Feature Data ─────────────────────────────────────────────────────────────
const features = [
  {
    tag: 'Acceso',
    headline: 'Tu condominio,\ntu código QR.',
    body: 'Olvídate de las llaves. Accede con tu teléfono, recibe visitantes sin complicaciones y genera códigos temporales en segundos.',
    visual: '🔐',
  },
  {
    tag: 'Transparencia',
    headline: 'Reportes financieros\nen tiempo real.',
    body: 'Cada peso entra y sale documentado. Tus residentes ven los estados de cuenta claros; los morosos quedan visibles para todos.',
    visual: '📊',
  },
  {
    tag: 'Comunidad',
    headline: 'Votaciones, eventos\ny avisos en un solo lugar.',
    body: 'Olvídate del WhatsApp. Anuncios oficiales, encuestas con quórum y calendario communal: todo organizado, todo a la vista.',
    visual: '🏢',
  },
  {
    tag: 'Cobranza',
    headline: 'Cobra en línea\nsin correr tras nadie.',
    body: 'Openpay integrado. Cargos automáticos, recordatorios suaves y historial completo de pagos. El dinero llega sin perseguirlo.',
    visual: '💳',
  },
];

const testimonials = [
  { quote: 'Antes gastábamos 4 horas al mes en reportes. Ahora se hacen solos.', author: 'María G.', role: 'Administradora, CDMX' },
  { quote: 'Mis residentes ahora pagan a tiempo porque ven exactamente a dónde va su dinero.', author: 'Carlos R.', role: 'Presidente de condomino, Monterrey' },
  { quote: 'La villa estaba en crisis y todo era un desorden. Vecindus nos dio orden en 2 semanas.', author: 'Ana L.', role: 'Directora, Bogotá', highlight: true },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [country, setCountry] = useState('default');
  const [condoUnits, setCondoUnits] = useState(15);
  const [isLoaded, setIsLoaded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Launch-readiness item 1 (2026-09-18): pre-launch waiting list capture.
  const [wlEmail, setWlEmail] = useState('');
  const [wlName, setWlName] = useState('');
  const [wlStatus, setWlStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [wlMessage, setWlMessage] = useState('');

  async function submitWaitingList(e: React.FormEvent) {
    e.preventDefault();
    if (wlStatus === 'sending') return;
    setWlStatus('sending');
    setWlMessage('');
    try {
      const r = await fetch('/api/waiting-list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: wlEmail, fullName: wlName || undefined }),
      });
      const x = await r.json().catch(() => null);
      if (r.ok && x?.success) {
        setWlStatus('done');
        setWlMessage(x.data?.duplicate
          ? 'Ya estabas en la lista — ¡gracias por tu interés!'
          : `¡Listo! Estás en la lista${x.data?.position ? ` (eres el número ${x.data.position})` : ''}. Te avisaremos al lanzar.`);
      } else {
        setWlStatus('error');
        setWlMessage(x?.error || 'No pudimos registrarte. Intenta de nuevo.');
      }
    } catch {
      setWlStatus('error');
      setWlMessage('No pudimos registrarte. Intenta de nuevo.');
    }
  }

  const [publicPlans, setPublicPlans] = useState<any[]>([]);
  useEffect(() => { fetch('/api/billing/plans').then(r => r.ok ? r.json() : null).then(x => setPublicPlans(x?.data?.plans || [])).catch(() => {}); }, []);

  useEffect(() => {
    setCountry(detectCountry());
    setIsLoaded(true);
  }, []);

  const cfg = countryConfig[country] || countryConfig.default;
  const condoSmallPlan = publicPlans.find(p => p.id === 'condo_small');
  const condoBrackets = bracketsOf(condoSmallPlan);
  const condoMonthlyPrice = condoPriceFromBrackets(condoBrackets, condoUnits);
  const condoTierLine = tierLine(condoBrackets);
  const condoRawPrice = (() => { const r = bracketPriceAt(condoBrackets, Math.max(0, Math.floor(condoUnits))); return r == null ? null : r / 100; })();
  const condoPlateauActive = condoMonthlyPrice != null && condoRawPrice != null && condoMonthlyPrice > condoRawPrice;
  const condoPlateauNext = condoPlateauActive ? nextImprovementUnits(condoBrackets, condoUnits, condoMonthlyPrice as number) : null;
  const homePlusPlan = publicPlans.find(p => p.id === 'home_plus');
  const homePlusPrice = homePlusPlan ? Number(homePlusPlan.price_usd ?? (Number(homePlusPlan.price_usd_cents) || 0) / 100) : null;
  const homeUltraPlan = publicPlans.find(p => p.id === 'home_ultra');
  // FIX 2026-09-18 (Jaime): no stale $9.99 fallback — null until /api/billing/plans loads; render shows a skeleton instead.
  const homeUltraPrice = homeUltraPlan ? Number(homeUltraPlan.price_usd_cents ?? (Number(homeUltraPlan.price_usd) * 100)) / 100 : null;
  const homeUnlimitedPlan = publicPlans.find(p => p.id === 'home_unlimited');
  const homeUnlimitedPrice = homeUnlimitedPlan ? Number(homeUnlimitedPlan.price_usd_cents ?? (Number(homeUnlimitedPlan.price_usd) * 100)) / 100 : null;

  if (!isLoaded) {
    return <div className="min-h-full bg-white flex items-center justify-center text-gray-400">Cargando…</div>;
  }

  return (
    <div className="bg-white text-gray-900 font-sans" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* ── NAV ─────────────────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200/50">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          {/* Logo — links to vecindus.com */}
          <a href="https://vecindus.com" className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
            <svg width="28" height="28" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="40" height="40" rx="10" fill="#36CCD8"/>
              <path d="M20 8L6 22L12 28L20 20L28 28L34 22L20 8Z" fill="white"/>
              <path d="M20 16L12 24L16 28L20 24L24 28L28 24L20 16Z" fill="white" fillOpacity="0.4"/>
            </svg>
            <span className="text-lg font-bold tracking-tight text-gray-900">Vecindus</span>
          </a>
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-500">
            <a href="#features" className="hover:text-gray-900 transition-colors">Características</a>
            <a href="#pricing" className="hover:text-gray-900 transition-colors">Precios</a>
            <a href="#testimonials" className="hover:text-gray-900 transition-colors">Casos reales</a>
          </div>
          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors px-1">Iniciar Sesión</Link>
            <Link to="/register" className="bg-[#36CCD8] hover:bg-[#2bb8c5] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all">Comenzar</Link>
          </div>
          {/* Mobile hamburger */}
          <button className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/></svg>
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-1">
            <a href="#features" className="block py-2.5 text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Características</a>
            <a href="#pricing" className="block py-2.5 text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Precios</a>
            <a href="#testimonials" className="block py-2.5 text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Casos reales</a>
            <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
              <Link to="/login" className="py-2.5 text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Iniciar Sesión</Link>
              <Link to="/register" className="bg-[#36CCD8] text-white text-sm font-semibold px-5 py-2.5 rounded-full text-center" onClick={() => setMobileMenuOpen(false)}>Comenzar</Link>
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <section className="pt-32 pb-24 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#36CCD8] text-sm font-semibold tracking-widest uppercase mb-5">Administración de condominios</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-tight mb-6">
            Tu vida.<br/>Tu comunidad.<br/><span className="text-[#36CCD8]">Vecindus.</span>
          </h1>
          <p className="text-xl sm:text-2xl text-gray-500 font-light max-w-xl mx-auto mb-10 leading-relaxed">
            La plataforma que transforma cómo se administra y se vive en un condominio.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="bg-[#36CCD8] hover:bg-[#2bb8c5] text-white font-semibold px-8 py-4 rounded-full text-lg transition-all shadow-lg shadow-[#36CCD8]/20">
              Probar gratis
            </Link>
            <a href="#features" className="border border-gray-300 hover:border-gray-400 text-gray-700 font-semibold px-8 py-4 rounded-full text-lg transition-all">
              Ver cómo funciona
            </a>
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────────────────── */}
      <section id="features" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          {features.map((f, i) => (
            <div key={i} className={`flex flex-col md:flex-row items-center gap-16 py-24 ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              {/* Text */}
              <div className="flex-1">
                <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4">{f.tag}</p>
                <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight whitespace-pre-line mb-6">{f.headline}</h2>
                <p className="text-xl text-gray-500 font-light leading-relaxed">{f.body}</p>
              </div>
              {/* Visual */}
              <div className="flex-1 flex justify-center">
                <div className="w-72 h-72 bg-gradient-to-br from-[#36CCD8]/10 to-[#36CCD8]/5 rounded-3xl flex items-center justify-center text-9xl">
                  {f.visual}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SOCIAL PROOF STRIP ───────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-[#36CCD8] text-white text-center">
        <div className="max-w-5xl mx-auto">
          <p className="text-white/70 text-sm font-medium tracking-widest uppercase mb-8">Usado por administradores en</p>
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 text-white/60 text-sm font-medium tracking-wide">
            {['México', 'Colombia', 'Venezuela', 'Perú', 'Chile', 'Argentina'].map(c => (
              <span key={c}>{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ────────────────────────────────────────────────────── */}
      <section id="testimonials" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4 text-center">Casos reales</p>
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-center mb-16">Lo que dicen los administradores</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className={`p-8 rounded-2xl border ${t.highlight ? 'border-[#36CCD8]/40 bg-[#36CCD8]/5' : 'border-gray-200 bg-white'}`}>
                <p className="text-lg font-light text-gray-700 leading-relaxed mb-6">"{t.quote}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{t.author}</p>
                  <p className="text-sm text-gray-500">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────────── */}
      <section id="pricing" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4">Precios</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-4">Simple y transparente</h2>
            <p className="text-xl text-gray-500 font-light">Sin sorpresas. Sin letra pequeña.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
            <div className="bg-white rounded-3xl p-8 border border-gray-200 flex flex-col">
              <h3 className="text-sm font-semibold tracking-widest uppercase text-gray-500 mb-2">Gratis</h3>
              <p className="text-5xl font-semibold tracking-tight mb-1">{formatPrice(0, country)}</p>
              <p className="text-gray-500 text-sm mb-8">para hogares</p>
              <ul className="space-y-3 text-sm text-gray-600 mb-10 flex-1">
                {['Funciones esenciales del hogar', 'Anuncios y acceso QR', 'Tareas y lista de compras'].map(l => <li key={l} className="flex items-start gap-2"><span className="text-[#36CCD8]">✓</span>{l}</li>)}
              </ul>
              <Link to="/register" className="block text-center border-2 border-gray-200 text-gray-700 font-semibold px-6 py-3.5 rounded-full text-sm">Comenzar gratis</Link>
            </div>
            <div className="bg-[#36CCD8] text-white rounded-3xl p-8 flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-[#36CCD8] text-xs font-bold px-4 py-1.5 rounded-full shadow">para tu hogar</div>
              <h3 className="text-sm font-semibold tracking-widest uppercase text-white/70 mb-2">Home+</h3>
              <p className="text-5xl font-semibold tracking-tight mb-1">{homePlusPrice != null ? formatPrice(homePlusPrice, country) : <span className="inline-block h-10 w-36 rounded bg-white/40 animate-pulse align-middle" aria-label="Cargando precio Home+"></span>}</p>
              <p className="text-white/70 text-sm mb-8">{country === 'MX' ? 'MXN' : 'USD'} por mes</p>
              <ul className="space-y-3 text-sm text-white/85 mb-10 flex-1">{['Tareas, mascotas y vehículos ilimitados', 'Presupuesto familiar', 'Seguridad del hogar', 'Todo lo del plan Gratis'].map(l => <li key={l} className="flex items-start gap-2"><span>✓</span>{l}</li>)}</ul>
              <Link to="/register" className="block text-center bg-white text-[#36CCD8] font-semibold px-6 py-3.5 rounded-full text-sm">Elegir Home+</Link>
            </div>
            <div className="bg-white rounded-3xl p-8 border-2 border-[#36CCD8] flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#36CCD8] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow">hogar premium</div>
              <h3 className="text-sm font-semibold tracking-widest uppercase text-gray-500 mb-2">HomeUltra</h3>
              <p className="text-4xl font-semibold tracking-tight mb-1">{homeUltraPrice != null ? formatPrice(homeUltraPrice, country) : <span className="inline-block h-10 w-28 rounded bg-gray-200 animate-pulse align-middle" aria-label="Cargando precio HomeUltra"></span>}</p>
              <p className="text-gray-500 text-sm mb-8">USD por mes</p>
              <ul className="space-y-3 text-sm text-gray-600 mb-10 flex-1">{['Todo lo del plan Home+', 'Hasta 9 unidades de tu propiedad', 'Todas las funciones del hogar desbloqueadas', 'Prioridad en soporte'].map(l => <li key={l} className="flex items-start gap-2"><span className="text-[#36CCD8]">✓</span>{l}</li>)}</ul>
              <Link to="/register" className="block text-center bg-[#36CCD8] text-white font-semibold px-6 py-3.5 rounded-full text-sm">Elegir HomeUltra</Link>
            </div>
            <div className="bg-white rounded-3xl p-8 border-2 border-amber-300 flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow">sin límite de unidades</div>
              <h3 className="text-sm font-semibold tracking-widest uppercase text-gray-500 mb-2">HomeUnlimited</h3>
              <p className="text-4xl font-semibold tracking-tight mb-1">{homeUnlimitedPrice != null ? formatPrice(homeUnlimitedPrice, country) : <span className="inline-block h-10 w-28 rounded bg-gray-200 animate-pulse align-middle" aria-label="Cargando precio HomeUnlimited"></span>}</p>
              <p className="text-gray-500 text-sm mb-8">USD por mes</p>
              <ul className="space-y-3 text-sm text-gray-600 mb-10 flex-1">{['Todo lo del plan HomeUltra', 'Unidades ilimitadas de tu propiedad', 'Sin límite de residentes ni personal', 'Prioridad en soporte'].map(l => <li key={l} className="flex items-start gap-2"><span className="text-amber-400">✓</span>{l}</li>)}</ul>
              <Link to="/register" className="block text-center bg-amber-400 text-white font-semibold px-6 py-3.5 rounded-full text-sm">Elegir HomeUnlimited</Link>
            </div>
            <div className="bg-white rounded-3xl p-8 border border-gray-200 flex flex-col">
              <h3 className="text-sm font-semibold tracking-widest uppercase text-gray-500 mb-2">Condominios</h3>
              <p className="text-5xl font-semibold tracking-tight mb-1">{condoMonthlyPrice != null ? formatPrice(condoMonthlyPrice, country) : <span className="inline-block h-10 w-36 rounded bg-gray-200 animate-pulse align-middle" aria-label="Cargando precio de condominios"></span>}</p>
              <p className="text-gray-500 text-sm mb-4">USD/mes según unidades</p>
              <label className="text-sm text-gray-600 mb-2" htmlFor="condo-units">Unidades:</label>
              <input id="condo-units" type="number" min="1" value={condoUnits} onChange={e => setCondoUnits(Math.max(1, Number(e.target.value) || 1))} className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-3 text-center font-semibold" />
              <input type="range" min="1" max="500" value={Math.min(condoUnits, 500)} onChange={e => setCondoUnits(Number(e.target.value))} className="w-full accent-[#36CCD8] mb-6" />
              <p className="text-xs text-gray-500 mb-3">{condoTierLine ?? 'Cargando tarifas…'}</p>{condoPlateauActive && condoPlateauNext != null && <p className="text-xs text-gray-400 mb-3">Precio congelado — mejora con la tarifa por volumen a partir de {condoPlateauNext} unidades</p>}<ul className="space-y-1 text-xs text-gray-600 mb-6"><li>✓ Votaciones, anuncios y amenidades</li><li>✓ Gastos, reportes financieros y morosos</li><li>✓ Reservas, calendario, quejas y reglamentos</li><li>✓ Seguridad, QR, pánico y viajes</li></ul>
              <Link to="/register" className="block text-center border-2 border-gray-200 text-gray-700 font-semibold px-6 py-3.5 rounded-full text-sm">Crear mi condominio</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── WAITING LIST (launch-readiness item 1, 2026-09-18) ─────────────────────── */}
      <section id="lista-de-espera" className="py-24 px-6 bg-gray-50">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4">Pre-lanzamiento</p>
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-4">Únete a la lista de espera</h2>
          <p className="text-xl text-gray-500 font-light mb-10">Sé de los primeros en probar Vecindus y recibe acceso prioritario al lanzamiento.</p>
          {wlStatus === 'done' ? (
            <div className="p-8 rounded-2xl border border-[#36CCD8]/40 bg-[#36CCD8]/5">
              <p className="text-5xl mb-4">🎉</p>
              <p className="text-lg font-semibold text-gray-900 mb-1">¡Registro confirmado!</p>
              <p className="text-gray-500">{wlMessage}</p>
            </div>
          ) : (
            <form onSubmit={submitWaitingList} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
              <input
                type="text"
                value={wlName}
                onChange={e => setWlName(e.target.value)}
                placeholder="Tu nombre (opcional)"
                maxLength={120}
                className="flex-1 border border-gray-300 rounded-full px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#36CCD8]/40"
              />
              <input
                type="email"
                required
                value={wlEmail}
                onChange={e => setWlEmail(e.target.value)}
                placeholder="tu@correo.com"
                className="flex-1 border border-gray-300 rounded-full px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#36CCD8]/40"
              />
              <button
                type="submit"
                disabled={wlStatus === 'sending'}
                className="bg-[#36CCD8] hover:bg-[#2bb8c5] disabled:opacity-60 text-white font-semibold px-8 py-3.5 rounded-full text-sm transition-all shadow-lg shadow-[#36CCD8]/20 whitespace-nowrap"
              >
                {wlStatus === 'sending' ? 'Enviando…' : 'Unirme'}
              </button>
            </form>
          )}
          {wlStatus === 'error' && <p className="text-sm text-red-500 mt-4">{wlMessage}</p>}
          {wlStatus !== 'done' && <p className="text-xs text-gray-400 mt-4">Sin spam. Solo te avisaremos cuando lancemos.</p>}
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-6">Tu condominio merece algo mejor.</h2>
          <p className="text-xl text-gray-500 font-light mb-10">Empieza hoy. Sin tarjeta, sin compromiso.</p>
          <Link to="/register" className="inline-block bg-[#36CCD8] hover:bg-[#2bb8c5] text-white font-semibold px-10 py-4 rounded-full text-lg transition-all shadow-lg shadow-[#36CCD8]/20">
            Crear mi condominio gratis
          </Link>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer className="border-t border-gray-200 py-12 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#36CCD8" strokeWidth="2.2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            <span className="font-semibold text-gray-900">Vecindus</span>
          </div>
          <p className="text-sm text-gray-400">© 2026 Vecindus. México · Colombia · Venezuela · Latinoamérica.</p>
          <div className="flex gap-6 text-sm text-gray-500">
            <a href="/privacidad" className="hover:text-gray-900 transition-colors">Privacidad</a>
            <a href="/terminos" className="hover:text-gray-900 transition-colors">Términos</a>
            <a href="#" className="hover:text-gray-900 transition-colors">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
